/**
 * Frontend observability · Wave 2 Bucket 2 PR 1.
 *
 * Sentry init must run BEFORE any other application code so unhandled
 * errors during React bootstrapping are captured. App.js imports this
 * module at the top of its import block; the side-effect `initSentry()`
 * call happens before React mounts.
 *
 * DSN policy:
 *   • `REACT_APP_SENTRY_DSN` empty/unset → init is a no-op (local dev,
 *     CI, preview environments don't need a DSN).
 *   • DSN set → Sentry captures unhandled errors + records the route +
 *     attaches user/tenant tags once auth resolves.
 *
 * PII scrubbing parallels the backend's `_scrub_event` in observability.py:
 *   • Authorization / Cookie / X-Test-* headers stripped from breadcrumbs
 *     and request data.
 *   • Request bodies on routes containing `/auth` or `/admin` are dropped.
 *   • User email + username dropped; user id retained for issue grouping.
 *
 * `tracesSampleRate: 0` by default — we are not shipping APM yet, only
 * error capture. When APM lands, lift via `REACT_APP_SENTRY_TRACES_RATE`
 * env var without re-deploying the code.
 */
import * as Sentry from "@sentry/react";

const SCRUB_HEADERS = new Set([
  "authorization", "cookie", "set-cookie", "x-api-key",
  "x-test-role", "x-test-tenant-id",
]);
const SCRUB_BODY_ROUTE_TOKENS = ["/auth", "/admin"];

function scrubEvent(event) {
  const req = event?.request;
  if (req?.headers && typeof req.headers === "object") {
    for (const key of Object.keys(req.headers)) {
      if (SCRUB_HEADERS.has(key.toLowerCase())) {
        req.headers[key] = "[scrubbed]";
      }
    }
  }
  if (req?.cookies) {
    req.cookies = "[scrubbed]";
  }
  const url = req?.url || "";
  if (typeof url === "string" && SCRUB_BODY_ROUTE_TOKENS.some(t => url.includes(t))) {
    if (req.data) req.data = "[scrubbed-auth-or-admin-route]";
    if (req.body) req.body = "[scrubbed-auth-or-admin-route]";
  }
  const user = event?.user;
  if (user) {
    delete user.email;
    delete user.username;
  }
  return event;
}

let _initialized = false;

export function initSentry() {
  const dsn = (process.env.REACT_APP_SENTRY_DSN || "").trim();
  if (!dsn || _initialized) return false;
  Sentry.init({
    dsn,
    environment: process.env.NODE_ENV || "development",
    release: process.env.REACT_APP_RELEASE_SHA || undefined,
    sendDefaultPii: false,
    tracesSampleRate: parseFloat(process.env.REACT_APP_SENTRY_TRACES_RATE || "0"),
    beforeSend: scrubEvent,
    beforeSendTransaction: scrubEvent,
  });
  _initialized = true;
  return true;
}

/**
 * Bind tenant_id + user_id to the Sentry scope. Call from the auth
 * callback once the WorkOS user resolves. Parallels the backend's
 * `bind_auth_context()` in observability.py.
 */
export function bindAuthContext({ userId, tenantId, role } = {}) {
  if (!_initialized) return;
  if (userId) Sentry.setUser({ id: userId });
  if (tenantId) Sentry.setTag("tenant_id", tenantId);
  if (role) Sentry.setTag("role", role);
}

// Exported for tests · pure function, no Sentry init required.
export const _scrubEventForTests = scrubEvent;
